import type { FC, ReactNode } from 'react';
import React, { useState, useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl-next';

import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import type { AvatarPropTypes } from '@atlaskit/avatar';
import AvatarGroup from '@atlaskit/avatar-group';
import PeopleGroupIcon from '@atlaskit/icon/core/people-group';
import { IconTile } from '@atlaskit/icon';
import { durations } from '@atlaskit/motion';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import { Bleed, Box, Inline, Pressable, Text, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import Tooltip from '@atlaskit/tooltip';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';

import { fg } from '@confluence/feature-gating';
import { expValEquals } from '@confluence/feature-experiments';

import type { GeneralAccessLevel } from '../../../../commonTypes';
import { SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE } from '../../../../../controller/Controller';
import type {
	ContentSubType,
	ContentType,
} from '../../../../../shareAndRestrictButton/ShareRestrictAndCopyCoordinator';
import { ConfluenceGroupUsageType } from '../../../../../controller/queries/__types__/useQueriesShareAndRestrictDialogQuery';

const restrictedI18n = defineMessages({
	twoUsers: {
		id: 'share-and-restrict-dialog.facepileRow.restricted.two-users',
		defaultMessage: '<b>{name1}</b> and <b>{name2}</b>',
		description: 'Text listing two users under Specific Access',
	},
	multipleUsers: {
		id: 'share-and-restrict-dialog.facepileRow.restricted.multiple-users',
		defaultMessage:
			'<b>{name1}, {name2},</b> and <b>{count} {count, plural, one {other} other {others}}</b>',
		description: 'Text listing multiple users under Specific Access',
	},
	youAndOneOther: {
		id: 'share-and-restrict-dialog.facepileRow.restricted.you-and-one-other',
		defaultMessage: '<b>You</b> and <b>{name}</b>',
		description: 'Text listing "You" and a second user under Specific Access',
	},
	youAndMultipleOthers: {
		id: 'share-and-restrict-dialog.facepileRow.restricted.you-and-multiple-others',
		defaultMessage:
			'<b>You, {name},</b> and <b>{count} {count, plural, one {other} other {others}}</b>',
		description: 'Text listing "You" and multiple other users under Specific Access',
	},
	deletedGroups: {
		id: 'share-and-restrict-dialog.facepileRow.restricted.deleted-groups',
		defaultMessage: 'This group was deleted',
		description: 'Text listing an unspecific deleted group under Specific Access',
	},
});

const openCanViewI18n = defineMessages({
	twoUsers: {
		id: 'share-and-restrict-dialog.facepileRow.open-can-view.two-users',
		defaultMessage: '<b>{name1}</b> and <b>{name2}</b> can edit',
		description: 'Text listing two users under Specific Access',
	},
	multipleUsers: {
		id: 'share-and-restrict-dialog.facepileRow.open-can-view.multiple-users',
		defaultMessage:
			'<b>{name1}, {name2},</b> and <b>{count} {count, plural, one {other} other {others}}</b> can edit',
		description: 'Text listing multiple users under Specific Access',
	},
	youAndOneOther: {
		id: 'share-and-restrict-dialog.facepileRow.open-can-view.you-and-one-other',
		defaultMessage: '<b>You</b> and <b>{name}</b> can edit',
		description: 'Text listing "You" and a second user under Specific Access',
	},
	youAndMultipleOthers: {
		id: 'share-and-restrict-dialog.facepileRow.open-can-view.you-and-multiple-others',
		defaultMessage:
			'<b>You, {name},</b> and <b>{count} {count, plural, one {other} other {others}}</b> can edit',
		description: 'Text listing "You" and multiple other users under Specific Access',
	},
});

const expandI18n = defineMessages({
	expand: {
		id: 'share-and-restrict-dialog.facepileRow.icon.expand',
		defaultMessage: 'View specific access',
		description:
			'Tooltip text for the expand icon in the Facepile component when Specific Access is collapsed',
	},
	collapse: {
		id: 'share-and-restrict-dialog.facepileRow.icon.collapse',
		defaultMessage: 'Collapse',
		description:
			'Tooltip text for the expand icon in the facepileRow component when Specific Access is expanded',
	},
	facepileExpand: {
		id: 'share-and-restrict-dialog.facepileRow.expand',
		defaultMessage: 'View specific access details',
		description: 'Aria label text for the Facepile component when Specific Access is collapsed',
	},
	facepileCollapse: {
		id: 'share-and-restrict-dialog.facepileRow.collapse',
		defaultMessage: 'Collapse specific access details',
		description: 'Aria label text for the Facepile component when Specific Access is expanded',
	},
});

const groupNameI18n = defineMessages({
	group: {
		id: 'share-and-restrict-dialog.facepileRow.group-name',
		defaultMessage: '{count} {count, plural, one {group} other {groups}}',
		description:
			'The name shown for the Group avatar in the Facepile component when any number of groups have access',
	},
});

const teamNameI18n = defineMessages({
	team: {
		id: 'share-and-restrict-dialog.facepileRow.team-name',
		defaultMessage: '{count} {count, plural, one {team} other {teams}}',
		description:
			'The name shown for the Team avatar in the Facepile component when any number of teams have access',
	},
});

const inlineStyle = xcss({
	paddingBlock: 'space.100',
	paddingInline: 'space.100',
	':hover': {
		background: token('elevation.surface.hovered'),
	},
	borderRadius: 'radius.small',
});

const chevronStyles = xcss({
	transform: 'rotate(0deg)',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values
	transition: `all ${durations.small}ms linear`,
});

const expandedStyles = xcss({
	transform: 'rotate(90deg)',
});

const expandBackgroundStyle = xcss({
	width: token('space.400'),
	height: token('space.400'),
	borderRadius: 'radius.small',
	paddingBlock: 'space.100',
	paddingInline: 'space.100',
	background: 'none',
	':hover': {
		background: token('color.background.neutral.subtle.hovered'),
	},
});

const bleedStyles = xcss({
	width: 'fill-available',
	paddingInlineEnd: 'space.050',
	height: '56px',
	paddingBlock: 'space.025',
});

const pressableStyles = xcss({
	padding: 'space.0',
	width: 'fill-available',
	background: 'none',
});

const iconBorderContainer = xcss({
	borderRadius: 'radius.full',
	width: '36px',
	height: '36px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexShrink: 0,
	marginTop: 'space.0',
	position: 'relative',
	transition: 'transform 200ms',
	// This zIndex is needed to ensure the Group icon overlaps properly with the surrounding Avatars
	// @ts-ignore style-maps.partial.tsx(1146, 2): The expected type comes from property 'zIndex' which is declared here on type 'SafeCSSObject'
	zIndex: 3,
	':active': {
		transform: 'scale(0.9)',
	},
	backgroundColor: 'elevation.surface.overlay',
});

const teamIconBorderContainer = xcss({
	borderRadius: 'radius.full',
	width: '36px',
	height: '36px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexShrink: 0,
	marginTop: 'space.0',
	position: 'relative',
	transition: 'transform 200ms',
	// This zIndex is needed to ensure the Group icon overlaps properly with the surrounding Avatars
	// @ts-ignore style-maps.partial.tsx(1146, 2): The expected type comes from property 'zIndex' which is declared here on type 'SafeCSSObject'
	zIndex: 4,
	':active': {
		transform: 'scale(0.9)',
	},
	backgroundColor: 'elevation.surface.overlay',
});

const iconContainer = xcss({
	borderRadius: 'radius.full',
	width: '32px',
	height: '32px',
	display: 'flex',
	justifyContent: 'center',
	alignItems: 'center',
	flexShrink: 0,
	marginTop: 'space.0',
	position: 'relative',

	'::after': {
		content: '""',
		backgroundColor: 'color.background.neutral.subtle',
		inset: '0px',

		/* Added border-radius style to fix hover issue in safari */
		borderRadius: 'radius.full',
		opacity: 0,
		pointerEvents: 'none',
		position: 'absolute',
		transition: 'opacity 200ms',
		width: '100%',
	},
	':hover': {
		// Need this to show the darkened overlay on hover
		// @ts-ignore Object literal may only specify known properties, and ''::after'' does not exist in type 'Omit<SafeCSSObject, Pseudos | AllMedia>'.
		'::after': {
			backgroundColor: 'color.background.inverse.subtle',
			opacity: 1,
		},
	},
});

const AVATAR_KEYS = {
	TEAM: 'teamAvatar',
	GROUP: 'groupAvatar',
} as const;

const AVATAR_TYPES = {
	USER: 'user',
	GROUP: 'group',
	TEAM: 'team',
} as const;

export type AvatarType = 'user' | 'group' | 'team';

export interface AvatarData extends AvatarPropTypes {
	name: string;
	key?: string | number;
	type: AvatarType;
	usageType?: ConfluenceGroupUsageType;
	src?: string;
}

export interface GroupAvatarData extends AvatarData {
	type: 'group' | 'team';
	usageType: ConfluenceGroupUsageType;
}

export type FacepileRowProps = {
	// Needed attributes: name, src, key
	avatarGroupData: AvatarData[];
	isExpanded: boolean;
	onExpandClick: () => void;
	onOpenSpecificAccessDialog: () => void;
	generalAccessLevel: GeneralAccessLevel;
	groupCount: number;
	teamCount: number;
	currentUserHasAccess: boolean;
	analyticsAttributes: {
		contentId: string;
		contentType: ContentType;
		contentSubType: ContentSubType | undefined;
		isLivePage: boolean;
		isDraft: boolean;
		isPublicLinkOn: boolean;
		pageMode: 'view' | 'edit';
	};
};

const noop = () => {
	/* This is intentional */
};

const calculateRemainingCount = (avatarGroupData: AvatarData[], groupCount: number): number => {
	const [, second, ...rest] = avatarGroupData;

	const nonGroups = rest.filter((avatar) => avatar.type !== AVATAR_TYPES.GROUP);

	let baseCount =
		// when the fg is off we want to make sure we treat group and teams as one
		!fg('confluence_frontend_perms_exp_teams_as_principals') && second?.type === AVATAR_TYPES.TEAM
			? nonGroups.length
			: rest.length;

	// If second avatar is a team and we have groups, we need to add the group count
	// since the team avatar represents all teams but groups are counted separately
	if (
		second?.type === AVATAR_TYPES.TEAM &&
		groupCount > 0 &&
		fg('confluence_frontend_perms_exp_teams_as_principals')
	) {
		baseCount += groupCount;
	}

	// If we have both team and group avatars in the list, we need to subtract 1
	// to avoid double counting since one of them is already represented in the avatar
	const hasGroupAvatar = avatarGroupData.some((avatar) => avatar.type === AVATAR_TYPES.GROUP);

	if (hasGroupAvatar && fg('confluence_frontend_perms_exp_teams_as_principals')) {
		// We have group avatars, so we need to account for the fact that
		// one group is represented in the avatarGroupData and shouldn't be double counted
		if (second?.type === AVATAR_TYPES.TEAM) {
			// Group avatar is shown, so subtract 1 from group count that's added to teams
			baseCount = baseCount - 1;
		}
	}

	return Math.max(0, baseCount);
};

const getGroupOrTeamDisplayName = (
	usageType: ConfluenceGroupUsageType | undefined,
	teamCount: number,
	groupCount: number,
	formatMessage: Function,
): string => {
	if (
		usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION &&
		fg('confluence_frontend_perms_exp_teams_as_principals')
	) {
		return formatMessage(teamNameI18n.team, { count: teamCount });
	}
	// if the FG is not on we go to default behavior to treating teams as groups
	else if (usageType === ConfluenceGroupUsageType.TEAM_COLLABORATION) {
		return formatMessage(groupNameI18n.group, { count: groupCount + teamCount });
	}

	return formatMessage(groupNameI18n.group, { count: groupCount });
};

const TeamAvatarIcon: FC<{
	teamCount: number;
	formatMessage: Function;
}> = ({ teamCount, formatMessage }) => (
	<Tooltip content={formatMessage(teamNameI18n.team, { count: teamCount })} position="bottom">
		{(tooltipProps) => (
			<Pressable {...tooltipProps} xcss={teamIconBorderContainer}>
				<Box xcss={iconContainer}>
					<IconTile
						label={formatMessage(teamNameI18n.team, { count: teamCount })}
						size="32"
						appearance="gray"
						shape="circle"
						icon={PeopleGroupIcon}
					/>
				</Box>
			</Pressable>
		)}
	</Tooltip>
);

const GroupAvatarIcon: FC<{
	groupCount: number;
	teamCount: number;
	formatMessage: Function;
}> = ({ groupCount, teamCount, formatMessage }) => (
	<Tooltip
		content={formatMessage(groupNameI18n.group, {
			count: fg('confluence_frontend_perms_exp_teams_as_principals')
				? groupCount
				: groupCount + teamCount,
		})}
		position="bottom"
	>
		{(tooltipProps) => (
			<Pressable
				{...tooltipProps}
				// This is needed because z-index needs to be updated since teams were added
				xcss={teamCount ? iconBorderContainer : teamIconBorderContainer}
			>
				<Box xcss={iconContainer}>
					<IconTile
						label={formatMessage(groupNameI18n.group, {
							count: fg('confluence_frontend_perms_exp_teams_as_principals')
								? groupCount
								: groupCount + teamCount,
						})}
						size="32"
						appearance="gray"
						shape="circle"
						icon={PeopleGroupIcon}
					/>
				</Box>
			</Pressable>
		)}
	</Tooltip>
);

const createAvatarRenderers = (teamCount: number, groupCount: number, formatMessage: Function) => ({
	[AVATAR_TYPES.TEAM]: (props: any, Component: any) => {
		if (props.src) {
			return (
				<Tooltip content={formatMessage(teamNameI18n.team, { count: teamCount })} position="bottom">
					<Box xcss={teamIconBorderContainer}>
						<Component {...props} />
					</Box>
				</Tooltip>
			);
		}
		return <TeamAvatarIcon teamCount={teamCount} formatMessage={formatMessage} />;
	},

	[AVATAR_TYPES.GROUP]: () => (
		<GroupAvatarIcon groupCount={groupCount} teamCount={teamCount} formatMessage={formatMessage} />
	),

	[AVATAR_TYPES.USER]: (props: any, Component: any) => <Component {...props} />,
});

export const FacepileRow: FC<FacepileRowProps> = ({
	avatarGroupData,
	isExpanded,
	onExpandClick,
	onOpenSpecificAccessDialog,
	generalAccessLevel,
	groupCount,
	teamCount,
	currentUserHasAccess,
	analyticsAttributes,
}) => {
	const [rowHovered, setRowHovered] = useState(false);
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const handleExpandClick = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				actionSubject: 'button',
				actionSubjectId: 'facepileRow',
				action: 'clicked',
				attributes: {
					...analyticsAttributes,
					generalAccessLevel,
					newState: isExpanded ? 'collapsed' : 'expanded',
				},
			},
		}).fire();
		onExpandClick();
	}, [createAnalyticsEvent, analyticsAttributes, generalAccessLevel, isExpanded, onExpandClick]);

	const handleOpenSpecificAccessDialog = useCallback(() => {
		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				source: SHARE_AND_RESTRICT_DIALOG_ANALYTICS_SOURCE,
				actionSubject: 'button',
				actionSubjectId: 'facepileRow',
				action: 'clicked',
				attributes: {
					...analyticsAttributes,
					generalAccessLevel,
					newState: 'openSpecificAccessDialog',
				},
			},
		}).fire();
		onOpenSpecificAccessDialog();
	}, [createAnalyticsEvent, analyticsAttributes, generalAccessLevel, onOpenSpecificAccessDialog]);

	const hasGroup = avatarGroupData.some((a) => a.type === AVATAR_TYPES.GROUP);
	const hasTeam = avatarGroupData.some((a) => a.type === AVATAR_TYPES.TEAM);

	const getFacepileDescription = () => {
		const [first, second] = avatarGroupData;

		// Handle single avatar edge case for groups/teams only
		if (avatarGroupData.length === 1) {
			if (first.type === AVATAR_TYPES.GROUP) {
				return (
					<Text weight="bold">
						{getGroupOrTeamDisplayName(first.usageType, teamCount, groupCount, formatMessage)}
					</Text>
				);
			}
			if (first.type === AVATAR_TYPES.TEAM) {
				return (
					<Text weight="bold">
						{getGroupOrTeamDisplayName(first.usageType, teamCount, groupCount, formatMessage)}
					</Text>
				);
			}
		}

		const firstName = first.name.split(' ')[0];
		let secondName: string = '';
		if (second) {
			secondName =
				second.type === AVATAR_TYPES.USER
					? second.name.split(' ')[0]
					: getGroupOrTeamDisplayName(second.usageType, teamCount, groupCount, formatMessage);
		}

		const remainingCount = calculateRemainingCount(avatarGroupData, groupCount);

		if (generalAccessLevel === 'restricted') {
			if (
				(!fg('confluence_frontend_perms_exp_teams_as_principals') &&
					avatarGroupData.length === 3 &&
					hasGroup &&
					hasTeam) ||
				avatarGroupData.length === 2
			) {
				if (currentUserHasAccess) {
					return formatMessage(restrictedI18n.youAndOneOther, {
						name: secondName,
						b: (chunk: ReactNode) => <Text weight="bold">{chunk}</Text>,
					});
				} else {
					return formatMessage(restrictedI18n.twoUsers, {
						name1: firstName,
						name2: secondName,
						b: (chunk: ReactNode) => <Text weight="bold">{chunk}</Text>,
					});
				}
			} else if (avatarGroupData.length > 2) {
				if (currentUserHasAccess) {
					return formatMessage(restrictedI18n.youAndMultipleOthers, {
						name: secondName,
						count: remainingCount,
						b: (chunk: ReactNode) => <Text weight="bold">{chunk}</Text>,
					});
				} else {
					return formatMessage(restrictedI18n.multipleUsers, {
						name1: firstName,
						name2: secondName,
						count: remainingCount,
						b: (chunk: ReactNode) => <Text weight="bold">{chunk}</Text>,
					});
				}
			}
		} else {
			if (
				(!fg('confluence_frontend_perms_exp_teams_as_principals') &&
					avatarGroupData.length === 3 &&
					hasGroup &&
					hasTeam) ||
				avatarGroupData.length === 2
			) {
				if (currentUserHasAccess) {
					return formatMessage(openCanViewI18n.youAndOneOther, {
						name: secondName,
						b: (chunk: ReactNode) => <Text weight="bold">{chunk}</Text>,
					});
				} else {
					return formatMessage(openCanViewI18n.twoUsers, {
						name1: firstName,
						name2: secondName,
						b: (chunk: ReactNode) => <Text weight="bold">{chunk}</Text>,
					});
				}
			} else if (avatarGroupData.length > 2) {
				if (currentUserHasAccess) {
					return formatMessage(openCanViewI18n.youAndMultipleOthers, {
						name: secondName,
						count: remainingCount,
						b: (chunk: ReactNode) => <Text weight="bold">{chunk}</Text>,
					});
				} else {
					return formatMessage(openCanViewI18n.multipleUsers, {
						name1: firstName,
						name2: secondName,
						count: remainingCount,
						b: (chunk: ReactNode) => <Text weight="bold">{chunk}</Text>,
					});
				}
			}
		}
	};

	const expandMessage = formatMessage(isExpanded ? expandI18n.collapse : expandI18n.expand);

	return (
		<Bleed inline="space.100" xcss={bleedStyles}>
			<Pressable
				onClick={
					expValEquals('persistence_specific_access_gate', 'cohort', 'experiment')
						? handleOpenSpecificAccessDialog
						: handleExpandClick
				}
				onMouseEnter={() => setRowHovered(true)}
				onMouseLeave={() => setRowHovered(false)}
				xcss={pressableStyles}
				aria-label={
					isExpanded
						? formatMessage(expandI18n.facepileCollapse)
						: formatMessage(expandI18n.facepileExpand)
				}
			>
				<Inline spread="space-between" alignBlock="center" xcss={inlineStyle}>
					<Inline alignBlock="center" space="space.100">
						{/* Remove any default click behaviors from the avatars and More button with noop */}
						<AvatarGroup
							data={avatarGroupData}
							onMoreClick={noop}
							onAvatarClick={noop}
							overrides={{
								Avatar: {
									render: (Component, props) => {
										const avatarRenderers = createAvatarRenderers(
											teamCount,
											groupCount,
											formatMessage,
										);

										// Check if this is a team avatar
										if (
											props.key === AVATAR_KEYS.TEAM &&
											fg('confluence_frontend_perms_exp_teams_as_principals')
										) {
											return avatarRenderers[AVATAR_TYPES.TEAM](props, Component);
										}

										// Check if this is a team avatar, if FG is off we need to treat teams as groups
										if (
											!fg('confluence_frontend_perms_exp_teams_as_principals') &&
											props.key === AVATAR_KEYS.TEAM &&
											!hasGroup
										) {
											return avatarRenderers[AVATAR_TYPES.GROUP]();
										}

										// Check if this is a group avatar
										if (props.key === AVATAR_KEYS.GROUP) {
											return avatarRenderers[AVATAR_TYPES.GROUP]();
										}

										// Check for user avatar as long as it not a team
										if (props.key !== AVATAR_KEYS.TEAM) {
											return avatarRenderers[AVATAR_TYPES.USER](props, Component);
										}

										// if we get to this point it means that Teams feature flag is OFF
										// and both groups and teams exist in avatarGroupData.
										// In that case, teams are treated as groups, so only
										// a single "group" avatar should be rendered.
									},
								},
							}}
						/>
						<Text>{getFacepileDescription()}</Text>
					</Inline>
					{rowHovered ? (
						<Tooltip content={expandMessage}>
							{(tooltipProps) => (
								<Pressable
									{...tooltipProps}
									aria-expanded={isExpanded}
									xcss={expandBackgroundStyle}
								>
									<Inline alignBlock="center" alignInline="center">
										<Box as="span" xcss={[chevronStyles, isExpanded && expandedStyles]}>
											<ChevronRightIcon label={expandMessage} size="small" />
										</Box>
									</Inline>
								</Pressable>
							)}
						</Tooltip>
					) : (
						<div />
					)}
				</Inline>
			</Pressable>
		</Bleed>
	);
};
